import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Slider } from "@/components/ui/slider";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { 
  Home, Building, Store, Map, Factory, Layers, Users, Shield,
  Plus, Search, CheckCircle, Clock, AlertCircle, Bell, Eye,
  Heart, TrendingUp, FileText, MapPin, DollarSign, Phone, Mail,
  User, ChevronRight, RefreshCw, Handshake, X, Check, Star,
  Sparkles, ClipboardList, Image as ImageIcon, Bed, Bath, Square, Calculator
} from "lucide-react";
import { VibeTag, getAllVibeTagsArray } from "@/components/VibeTag";
import { PropertyQuestionnaire } from "@/components/PropertyQuestionnaire";
import ImageUploader from "@/components/ImageUploader";
import { PropertyDetailModal } from "@/components/PropertyDetailModal";
import ValuationModule from "@/components/ValuationModule";

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

// Property Type Icons
const PROPERTY_TYPE_ICONS = {
  residential: Home,
  apartment: Building,
  commercial: Store,
  land: Map,
  industrial: Factory,
  mixed: Layers
};

// Municipalities list
import { MEXICAN_CITIES as MUNICIPALITIES } from "@/constants/mexicanCities";

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusConfig = {
    pending: { label: "Pendiente", color: "bg-yellow-100 text-yellow-700", icon: Clock },
    agent_assigned: { label: "Agente Asignado", color: "bg-blue-100 text-blue-700", icon: Users },
    commission_agreed: { label: "Comisión Acordada", color: "bg-purple-100 text-purple-700", icon: Handshake },
    property_listed: { label: "Publicada", color: "bg-green-100 text-green-700", icon: CheckCircle },
    matched: { label: "Con Matches", color: "bg-primary/20 text-primary", icon: Heart },
    sold: { label: "Vendida", color: "bg-emerald-100 text-emerald-700", icon: Star },
    cancelled: { label: "Cancelada", color: "bg-red-100 text-red-700", icon: X }
  };

  const config = statusConfig[status] || statusConfig.pending;
  const Icon = config.icon;

  return (
    <Badge className={`${config.color} gap-1`}>
      <Icon className="w-3 h-3" />
      {config.label}
    </Badge>
  );
};

// Agent Card Component
const AgentCard = ({ agent, onSelect, selected }) => (
  <Card 
    className={`cursor-pointer transition-all ${selected ? "ring-2 ring-primary" : "hover:shadow-md"}`}
    onClick={() => onSelect(agent)}
  >
    <CardContent className="p-4">
      <div className="flex items-start gap-4">
        <div className={`w-12 h-12 rounded-full flex items-center justify-center ${agent.verified ? "bg-green-100" : "bg-slate-100"}`}>
          {agent.verified ? <Shield className="w-6 h-6 text-green-600" /> : <User className="w-6 h-6 text-slate-500" />}
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-[#384451]">{agent.name}</h3>
          {agent.agency_name && (
            <p className="text-sm text-slate-500">{agent.agency_name}</p>
          )}
          <div className="flex items-center gap-2 mt-2 text-xs text-slate-500">
            <MapPin className="w-3 h-3" />
            <span>{agent.municipality || "Sin territorio definido"}</span>
          </div>
          <div className="flex items-center gap-2 mt-1 text-xs text-slate-500">
            <Building className="w-3 h-3" />
            <span>{agent.property_count} propiedades activas</span>
          </div>
          {agent.certifications?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-2">
              {agent.certifications.slice(0, 2).map((cert, idx) => (
                <Badge key={idx} variant="outline" className="text-xs">{cert}</Badge>
              ))}
            </div>
          )}
        </div>
        {selected && (
          <CheckCircle className="w-6 h-6 text-primary" />
        )}
      </div>
    </CardContent>
  </Card>
);

// Sale Intent Form Component
const SaleIntentForm = ({ onSubmit, onCancel }) => {
  const [formData, setFormData] = useState({
    property_type: "",
    municipality: "",
    address: "",
    estimated_price: "",
    surface_area_m2: "",
    construction_area_m2: "",
    notes: "",
    selected_agent_id: "",
    psychographic_attributes: null,
    images: []
  });
  const [agents, setAgents] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState(null);
  const [step, setStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [searchMunicipality, setSearchMunicipality] = useState("");
  const [uploadedImages, setUploadedImages] = useState([]);

  useEffect(() => {
    const fetchAgents = async () => {
      try {
        const response = await axios.get(`${API}/owner/available-agents`);
        setAgents(response.data);
      } catch (error) {
        console.error("Error fetching agents:", error);
      }
    };
    fetchAgents();
  }, []);

  const handleAgentSelect = (agent) => {
    setSelectedAgent(agent);
    setFormData({ ...formData, selected_agent_id: agent.id });
  };

  const handleSubmit = async () => {
    if (!formData.property_type || !formData.municipality || !formData.address || !formData.selected_agent_id) {
      toast.error("Por favor completa todos los campos obligatorios");
      return;
    }
    
    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        estimated_price: formData.estimated_price ? parseInt(formData.estimated_price) : null,
        surface_area_m2: formData.surface_area_m2 ? parseInt(formData.surface_area_m2) : null,
        construction_area_m2: formData.construction_area_m2 ? parseInt(formData.construction_area_m2) : null,
        psychographic_attributes: formData.psychographic_attributes
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuestionnaireComplete = (attributes) => {
    setFormData(prev => ({ ...prev, psychographic_attributes: attributes }));
    toast.success("¡Cuestionario completado! Ahora puedes enviar tu solicitud.");
  };

  const handleSkipQuestionnaire = () => {
    toast.info("Puedes completar el cuestionario más tarde. El agente también podrá ayudarte.");
    // Proceed without questionnaire data
  };

  const filteredAgents = searchMunicipality 
    ? agents.filter(a => a.municipality?.toLowerCase().includes(searchMunicipality.toLowerCase()))
    : agents;

  const PropertyTypeIcon = formData.property_type ? PROPERTY_TYPE_ICONS[formData.property_type] : Home;

  return (
    <div className="space-y-6">
      {/* Progress Steps */}
      <div className="flex items-center justify-between mb-8 overflow-x-auto pb-2">
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${step >= 1 ? "bg-primary text-[#384451]" : "bg-slate-200"}`}>
            <Home className="w-4 h-4" />
          </div>
          <span className={`text-sm ${step >= 1 ? "font-medium" : "text-slate-400"}`}>Propiedad</span>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${step >= 2 ? "bg-primary text-[#384451]" : "bg-slate-200"}`}>
            <Users className="w-4 h-4" />
          </div>
          <span className={`text-sm ${step >= 2 ? "font-medium" : "text-slate-400"}`}>Agente</span>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${step >= 3 ? "bg-primary text-[#384451]" : "bg-slate-200"}`}>
            <ClipboardList className="w-4 h-4" />
          </div>
          <span className={`text-sm ${step >= 3 ? "font-medium" : "text-slate-400"}`}>Cuestionario</span>
        </div>
        <ChevronRight className="w-4 h-4 text-slate-300 flex-shrink-0" />
        <div className="flex items-center gap-2 flex-shrink-0">
          <div className={`w-9 h-9 rounded-full flex items-center justify-center ${step >= 4 ? "bg-primary text-[#384451]" : "bg-slate-200"}`}>
            <CheckCircle className="w-4 h-4" />
          </div>
          <span className={`text-sm ${step >= 4 ? "font-medium" : "text-slate-400"}`}>Confirmar</span>
        </div>
      </div>

      {/* Step 1: Property Details */}
      {step === 1 && (
        <Card>
          <CardHeader>
            <CardTitle>Información de la Propiedad</CardTitle>
            <CardDescription>Cuéntanos sobre tu propiedad para encontrar el mejor agente</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Label>Tipo de Propiedad *</Label>
              <div className="grid grid-cols-3 gap-3 mt-2">
                {[
                  { id: "residential", name: "Residencial", icon: Home },
                  { id: "apartment", name: "Departamento", icon: Building },
                  { id: "commercial", name: "Comercial", icon: Store },
                  { id: "land", name: "Terreno", icon: Map },
                  { id: "industrial", name: "Industrial", icon: Factory },
                  { id: "mixed", name: "Uso Mixto", icon: Layers }
                ].map(type => (
                  <Button
                    key={type.id}
                    variant={formData.property_type === type.id ? "default" : "outline"}
                    className={`flex-col h-20 ${formData.property_type === type.id ? "bg-primary text-[#384451]" : ""}`}
                    onClick={() => setFormData({ ...formData, property_type: type.id })}
                  >
                    <type.icon className="w-6 h-6 mb-1" />
                    <span className="text-xs">{type.name}</span>
                  </Button>
                ))}
              </div>
            </div>

            <div>
              <Label>Colonia / Municipio *</Label>
              <Select value={formData.municipality} onValueChange={(v) => setFormData({ ...formData, municipality: v })}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Selecciona la ubicación" />
                </SelectTrigger>
                <SelectContent>
                  {MUNICIPALITIES.map(m => (
                    <SelectItem key={m} value={m}>{m}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Dirección Completa *</Label>
              <Input
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                placeholder="Calle, número, colonia, ciudad"
                className="mt-1"
              />
              <p className="text-xs text-slate-500 mt-1">Esta información se mantiene privada hasta firmar el acuerdo</p>
            </div>

            <div>
              <Label>Precio Estimado (MXN)</Label>
              <Input
                type="number"
                value={formData.estimated_price}
                onChange={(e) => setFormData({ ...formData, estimated_price: e.target.value })}
                placeholder="Ej: 5000000"
                className="mt-1"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Metros cuadrados de terreno</Label>
                <Input
                  type="number"
                  value={formData.surface_area_m2}
                  onChange={(e) => setFormData({ ...formData, surface_area_m2: e.target.value })}
                  placeholder="Ej: 250"
                  className="mt-1"
                  data-testid="surface-area-input"
                />
              </div>
              <div>
                <Label>Metros cuadrados de construcción</Label>
                <Input
                  type="number"
                  value={formData.construction_area_m2}
                  onChange={(e) => setFormData({ ...formData, construction_area_m2: e.target.value })}
                  placeholder="Ej: 180"
                  className="mt-1"
                  data-testid="construction-area-input"
                />
              </div>
            </div>

            <div>
              <Label>Notas Adicionales</Label>
              <Textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                placeholder="Características especiales, urgencia de venta, etc."
                rows={3}
                className="mt-1"
              />
            </div>

            {/* Image Upload */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <ImageIcon className="w-4 h-4" />
                Fotografías de la Propiedad (Opcional)
              </Label>
              <ImageUploader
                images={uploadedImages}
                onChange={(newImages) => {
                  setUploadedImages(newImages);
                  const imageUrls = newImages.map(img => img.dataUrl || img);
                  setFormData({...formData, images: imageUrls});
                }}
                maxImages={10}
                maxSizeKB={500}
              />
              <p className="text-xs text-slate-500">
                Las fotografías ayudan al agente a evaluar mejor tu propiedad
              </p>
            </div>

            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={onCancel}>Cancelar</Button>
              <Button 
                onClick={() => setStep(2)} 
                disabled={!formData.property_type || !formData.municipality || !formData.address}
                className="bg-primary text-[#384451]"
              >
                Siguiente: Seleccionar Agente <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 2: Select Agent */}
      {step === 2 && (
        <Card>
          <CardHeader>
            <CardTitle>Selecciona tu Agente Representante</CardTitle>
            <CardDescription>
              El agente será responsable de publicar y promover tu propiedad. 
              <strong className="text-primary"> Tú defines la comisión mínima del 3%</strong> que pagarás al agente sobre el precio de venta.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar por zona..."
                value={searchMunicipality}
                onChange={(e) => setSearchMunicipality(e.target.value)}
                className="pl-10"
              />
            </div>

            <ScrollArea className="h-[400px]">
              <div className="grid grid-cols-2 gap-4">
                {filteredAgents.map(agent => (
                  <AgentCard 
                    key={agent.id} 
                    agent={agent} 
                    onSelect={handleAgentSelect}
                    selected={selectedAgent?.id === agent.id}
                  />
                ))}
              </div>
              {filteredAgents.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Users className="w-12 h-12 mx-auto mb-3 opacity-50" />
                  <p>No hay agentes disponibles en esta zona</p>
                </div>
              )}
            </ScrollArea>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(1)}>Anterior</Button>
              <Button 
                onClick={() => setStep(3)} 
                disabled={!selectedAgent}
                className="bg-primary text-[#384451]"
              >
                Siguiente: Cuestionario <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Step 3: Property Questionnaire (Optional) */}
      {step === 3 && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Sparkles className="w-5 h-5 text-primary" />
              ADN de tu Propiedad (Opcional)
            </CardTitle>
            <CardDescription>
              Este cuestionario de 52 factores ayuda a encontrar compradores más compatibles con tu propiedad.
              Es <strong>opcional</strong> - si prefieres, tu agente puede completarlo después.
            </CardDescription>
          </CardHeader>
          <CardContent>
            <PropertyQuestionnaire
              mode="owner"
              onComplete={handleQuestionnaireComplete}
              onSkipAll={() => {
                handleSkipQuestionnaire();
                setStep(4);
              }}
            />
          </CardContent>
          <CardFooter className="flex justify-between border-t pt-4">
            <Button variant="outline" onClick={() => setStep(2)}>Anterior</Button>
            <div className="flex gap-2">
              <Button 
                variant="outline"
                onClick={() => setStep(4)}
              >
                Omitir y Continuar
              </Button>
              {formData.psychographic_attributes && (
                <Button 
                  onClick={() => setStep(4)}
                  className="bg-primary text-[#384451]"
                >
                  Continuar a Confirmación <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              )}
            </div>
          </CardFooter>
        </Card>
      )}

      {/* Step 4: Confirm */}
      {step === 4 && (
        <Card>
          <CardHeader>
            <CardTitle>Confirma tu Solicitud</CardTitle>
            <CardDescription>Revisa los detalles antes de enviar</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            {/* Property Summary */}
            <div className="p-4 bg-slate-50 rounded-lg">
              <h4 className="font-medium text-[#384451] mb-3">Tu Propiedad</h4>
              <div className="grid grid-cols-2 gap-4">
                <div className="flex items-center gap-2">
                  <PropertyTypeIcon className="w-5 h-5 text-primary" />
                  <span className="text-sm capitalize">{formData.property_type}</span>
                </div>
                <div className="flex items-center gap-2">
                  <MapPin className="w-5 h-5 text-primary" />
                  <span className="text-sm">{formData.municipality}</span>
                </div>
                {formData.estimated_price && (
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-primary" />
                    <span className="text-sm">${parseInt(formData.estimated_price).toLocaleString()} MXN</span>
                  </div>
                )}
              </div>
            </div>

            {/* Selected Agent */}
            {selectedAgent && (
              <div className="p-4 bg-slate-50 rounded-lg">
                <h4 className="font-medium text-[#384451] mb-3">Agente Seleccionado</h4>
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                    <Shield className="w-6 h-6 text-primary" />
                  </div>
                  <div>
                    <p className="font-medium">{selectedAgent.name}</p>
                    {selectedAgent.agency_name && (
                      <p className="text-sm text-slate-500">{selectedAgent.agency_name}</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Commission Info */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <h4 className="font-medium text-amber-800 mb-2 flex items-center gap-2">
                <Handshake className="w-5 h-5" />
                Acuerdo de Comisión
              </h4>
              <p className="text-sm text-amber-700">
                Al enviar esta solicitud, aceptas pagar una comisión mínima del <strong>3%</strong> al agente sobre el precio de venta. 
                Podrás negociar el porcentaje final con el agente antes de firmar el acuerdo (sugerido entre 4-6%).
              </p>
            </div>

            {/* Questionnaire Status */}
            <div className={`p-4 rounded-lg border ${formData.psychographic_attributes ? "bg-green-50 border-green-200" : "bg-slate-50 border-slate-200"}`}>
              <h4 className={`font-medium mb-2 flex items-center gap-2 ${formData.psychographic_attributes ? "text-green-800" : "text-slate-600"}`}>
                <ClipboardList className="w-5 h-5" />
                Cuestionario de 52 Factores
              </h4>
              {formData.psychographic_attributes ? (
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-4 h-4 text-green-500" />
                  <p className="text-sm text-green-700">Completado - Tu propiedad tendrá mejor matching con compradores</p>
                </div>
              ) : (
                <div className="flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-slate-400" />
                  <p className="text-sm text-slate-500">No completado - Tu agente podrá completarlo después</p>
                </div>
              )}
            </div>

            {/* Privacy Notice */}
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="font-medium text-blue-800 mb-2 flex items-center gap-2">
                <Shield className="w-5 h-5" />
                Privacidad de tus Datos
              </h4>
              <ul className="text-sm text-blue-700 space-y-1">
                <li>• Tu dirección y datos de contacto permanecerán ocultos hasta firmar el acuerdo</li>
                <li>• Solo el agente seleccionado verá tus datos después del acuerdo</li>
                <li>• Los compradores <strong>nunca</strong> verán tu información de contacto</li>
              </ul>
            </div>

            <div className="flex justify-between">
              <Button variant="outline" onClick={() => setStep(3)}>Anterior</Button>
              <Button 
                onClick={handleSubmit} 
                disabled={loading}
                className="bg-primary text-[#384451]"
                data-testid="submit-sale-intent-btn"
              >
                {loading ? "Enviando..." : <><CheckCircle className="w-4 h-4 mr-2" /> Enviar Solicitud</>}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Commission Proposal Dialog
const CommissionDialog = ({ intent, onClose, onSubmit }) => {
  const [commission, setCommission] = useState(intent?.commission_percentage || 3);
  const [terms, setTerms] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await onSubmit({
        sale_intent_id: intent.id,
        commission_percentage: commission,
        additional_terms: terms || null
      });
      onClose();
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={!!intent} onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Proponer Comisión al Agente</DialogTitle>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div>
            <Label>Porcentaje de Comisión que pagarás al agente</Label>
            <div className="flex items-center gap-4 mt-2">
              <Slider
                value={[commission]}
                onValueChange={([v]) => setCommission(Math.max(3, v))}
                min={3}
                max={10}
                step={0.5}
                className="flex-1"
              />
              <span className="text-2xl font-bold text-primary w-16 text-right">{commission}%</span>
            </div>
            <p className="text-xs text-slate-500 mt-1">Mínimo 3% (del precio de venta), sugerido 4-6%</p>
          </div>

          {intent?.estimated_price && (
            <div className="p-3 bg-slate-50 rounded-lg">
              <p className="text-sm text-slate-600">
                Comisión estimada: <strong className="text-primary">
                  ${Math.round(intent.estimated_price * (commission / 100)).toLocaleString()} MXN
                </strong>
              </p>
            </div>
          )}

          <div>
            <Label>Términos Adicionales (opcional)</Label>
            <Textarea
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              placeholder="Condiciones especiales, fechas límite, etc."
              rows={3}
              className="mt-1"
            />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} disabled={loading} className="bg-primary text-[#384451]">
            {loading ? <RefreshCw className="w-4 h-4 animate-spin mr-2" /> : null}
            Enviar Propuesta
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

// Property Stats Card
const PropertyStatsCard = ({ stats }) => {
  if (!stats) return null;

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-lg">{stats.title}</CardTitle>
        <CardDescription>Estadísticas de tu propiedad</CardDescription>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="text-center p-3 bg-blue-50 rounded-lg">
            <Eye className="w-6 h-6 mx-auto text-blue-500 mb-1" />
            <p className="text-2xl font-bold">{stats.stats?.views || 0}</p>
            <p className="text-xs text-slate-500">Visualizaciones</p>
          </div>
          <div className="text-center p-3 bg-green-50 rounded-lg">
            <Heart className="w-6 h-6 mx-auto text-green-500 mb-1" />
            <p className="text-2xl font-bold">{stats.stats?.likes || 0}</p>
            <p className="text-xs text-slate-500">Likes</p>
          </div>
          <div className="text-center p-3 bg-primary/10 rounded-lg">
            <TrendingUp className="w-6 h-6 mx-auto text-primary mb-1" />
            <p className="text-2xl font-bold">{stats.stats?.total_matches || 0}</p>
            <p className="text-xs text-slate-500">Matches</p>
          </div>
          <div className="text-center p-3 bg-purple-50 rounded-lg">
            <Star className="w-6 h-6 mx-auto text-purple-500 mb-1" />
            <p className="text-2xl font-bold">{Math.round(stats.stats?.avg_match_score || 0)}%</p>
            <p className="text-xs text-slate-500">Match Promedio</p>
          </div>
        </div>

        {stats.vibe_tags?.length > 0 && (
          <div className="mt-4">
            <p className="text-sm font-medium mb-2">Vibe Tags de tu propiedad:</p>
            <div className="flex flex-wrap gap-2">
              {stats.vibe_tags.map(tagId => {
                const tag = getAllVibeTagsArray().find(t => t.id === tagId);
                return tag ? <VibeTag key={tagId} tag={tag} size="sm" /> : null;
              })}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Main Owner Dashboard Component
export const OwnerDashboard = () => {
  const [activeTab, setActiveTab] = useState("overview");
  const [profile, setProfile] = useState(null);
  const [saleIntents, setSaleIntents] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewIntent, setShowNewIntent] = useState(false);
  const [selectedIntent, setSelectedIntent] = useState(null);
  const [showCommission, setShowCommission] = useState(false);
  const [propertyStats, setPropertyStats] = useState(null);
  
  // Property detail modal state
  const [showPropertyDetailModal, setShowPropertyDetailModal] = useState(false);
  const [selectedPropertyDetail, setSelectedPropertyDetail] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [profileRes, intentsRes, notificationsRes] = await Promise.all([
        axios.get(`${API}/owner/profile`),
        axios.get(`${API}/owner/sale-intents`),
        axios.get(`${API}/owner/notifications`)
      ]);

      setProfile(profileRes.data);
      setSaleIntents(intentsRes.data);
      setNotifications(notificationsRes.data);
    } catch (error) {
      console.error("Error fetching owner data:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const fetchPropertyStats = async (intentId) => {
    try {
      const response = await axios.get(`${API}/owner/property-stats/${intentId}`);
      setPropertyStats(response.data);
    } catch (error) {
      console.error("Error fetching property stats:", error);
    }
  };

  // View property full details
  const viewPropertyDetail = (intent) => {
    if (intent.property) {
      setSelectedPropertyDetail({
        property: {
          ...intent.property,
          id: intent.property.id || intent.id,
          title: intent.property.title || intent.property_title,
          story: intent.property.story || intent.property.description,
          status: intent.status
        },
        statistics: {
          views: intent.property.views || 0,
          total_swipes: intent.property.swipes_right || 0,
          total_matches: intent.matches_count || 0
        }
      });
      setShowPropertyDetailModal(true);
    }
  };

  const handleCreateIntent = async (data) => {
    try {
      const response = await axios.post(`${API}/owner/sale-intent`, data);
      toast.success(response.data.message);
      setShowNewIntent(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al crear solicitud");
    }
  };

  const handleProposeCommission = async (data) => {
    try {
      const response = await axios.post(`${API}/owner/commission/propose`, data);
      toast.success(response.data.message);
      setShowCommission(false);
      setSelectedIntent(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Error al enviar propuesta");
    }
  };

  const markNotificationRead = async (notificationId) => {
    try {
      await axios.put(`${API}/owner/notifications/${notificationId}/read`);
      setNotifications(notifications.map(n => 
        n.id === notificationId ? { ...n, read: true } : n
      ));
    } catch (error) {
      console.error("Error marking notification read:", error);
    }
  };

  const unreadNotifications = notifications.filter(n => !n.read).length;

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <RefreshCw className="w-10 h-10 text-primary animate-spin" />
      </div>
    );
  }

  // New Intent Form
  if (showNewIntent) {
    return (
      <div className="min-h-screen bg-slate-100 p-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-2xl font-bold text-[#384451] mb-6">Registrar Intención de Venta</h1>
          <SaleIntentForm 
            onSubmit={handleCreateIntent}
            onCancel={() => setShowNewIntent(false)}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-100 flex">
      {/* Sidebar */}
      <aside className="w-64 bg-slate-900 text-white p-6 flex flex-col">
        <div className="mb-8">
          <h1 className="text-xl font-bold text-primary">Findperty</h1>
          <p className="text-sm text-slate-400">Portal de Propietario</p>
        </div>

        {/* Profile Summary */}
        <div className="mb-6 p-4 bg-slate-800 rounded-lg">
          <div className="flex items-center gap-3 mb-3">
            <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-primary" />
            </div>
            <div>
              <p className="font-medium text-sm">{profile?.name}</p>
              <p className="text-xs text-slate-400">{profile?.email}</p>
            </div>
          </div>
          <div className="text-xs text-slate-400 space-y-1">
            <p>Propiedades: <span className="text-primary font-medium">{profile?.sale_intents_count || 0}</span></p>
            <p>Activas: <span className="text-green-400 font-medium">{profile?.active_listings || 0}</span></p>
          </div>
        </div>

        <nav className="space-y-2 flex-1">
          {[
            { id: "overview", label: "Resumen", icon: Home },
            { id: "properties", label: "Mis Propiedades", icon: Building },
            { id: "valuation", label: "Valuación MVIP", icon: Calculator },
            { id: "terreno", label: "Potencial Terreno", icon: Map },
            { id: "notifications", label: "Notificaciones", icon: Bell, badge: unreadNotifications }
          ].map(item => (
            <button
              key={item.id}
              onClick={() => setActiveTab(item.id)}
              className={`w-full flex items-center gap-3 px-4 py-3 rounded-lg transition-colors ${
                activeTab === item.id ? "bg-primary text-slate-900 font-medium" : "text-slate-300 hover:bg-slate-800"
              }`}
            >
              <item.icon className="w-5 h-5" />
              <span>{item.label}</span>
              {item.badge > 0 && (
                <Badge className="ml-auto bg-red-500">{item.badge}</Badge>
              )}
            </button>
          ))}
        </nav>

        <div className="pt-4 border-t border-slate-700">
          <Button 
            className="w-full bg-primary text-slate-900 hover:bg-[#b8c100]"
            onClick={() => setShowNewIntent(true)}
          >
            <Plus className="w-4 h-4 mr-2" /> Vender Propiedad
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 p-8 overflow-auto">
        {/* Overview Tab */}
        {activeTab === "overview" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-2xl font-bold text-[#384451]">Bienvenido, {profile?.name?.split(' ')[0]}</h2>
                <p className="text-slate-500">Gestiona tus propiedades en venta</p>
              </div>
              <Button onClick={() => setShowNewIntent(true)} className="bg-primary text-[#384451]">
                <Plus className="w-4 h-4 mr-2" /> Nueva Propiedad
              </Button>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-3 gap-4">
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Total Propiedades</p>
                      <p className="text-2xl font-bold">{saleIntents.length}</p>
                    </div>
                    <Building className="w-8 h-8 text-primary" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Publicadas</p>
                      <p className="text-2xl font-bold">
                        {saleIntents.filter(i => i.status === "property_listed" || i.status === "matched").length}
                      </p>
                    </div>
                    <CheckCircle className="w-8 h-8 text-green-500" />
                  </div>
                </CardContent>
              </Card>
              <Card>
                <CardContent className="p-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-slate-500">Pendientes</p>
                      <p className="text-2xl font-bold">
                        {saleIntents.filter(i => i.status === "pending" || i.status === "agent_assigned").length}
                      </p>
                    </div>
                    <Clock className="w-8 h-8 text-yellow-500" />
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent Activity */}
            <Card>
              <CardHeader>
                <CardTitle>Actividad Reciente</CardTitle>
              </CardHeader>
              <CardContent>
                {saleIntents.length === 0 ? (
                  <div className="text-center py-8">
                    <Building className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">Aún no tienes propiedades registradas</p>
                    <Button onClick={() => setShowNewIntent(true)} className="mt-4 bg-primary text-[#384451]">
                      <Plus className="w-4 h-4 mr-2" /> Registrar Primera Propiedad
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {saleIntents.slice(0, 3).map(intent => (
                      <div key={intent.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                        <div className="flex items-center gap-4">
                          <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center">
                            {(() => {
                              const Icon = PROPERTY_TYPE_ICONS[intent.property_type] || Home;
                              return <Icon className="w-5 h-5 text-primary" />;
                            })()}
                          </div>
                          <div>
                            <p className="font-medium text-[#384451] capitalize">
                              {intent.property_type} en {intent.municipality}
                            </p>
                            <p className="text-sm text-slate-500">
                              Agente: {intent.agent_name || "Pendiente"}
                            </p>
                          </div>
                        </div>
                        <StatusBadge status={intent.status} />
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* How it Works */}
            <Card>
              <CardHeader>
                <CardTitle>¿Cómo Funciona?</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid grid-cols-4 gap-4">
                  {[
                    { step: 1, title: "Registra tu propiedad", desc: "Ingresa los datos básicos", icon: FileText },
                    { step: 2, title: "Elige un agente", desc: "Selecciona tu representante", icon: Users },
                    { step: 3, title: "Acuerda comisión", desc: "Negocia los términos", icon: Handshake },
                    { step: 4, title: "Recibe ofertas", desc: "El agente gestiona todo", icon: Heart }
                  ].map(item => (
                    <div key={item.step} className="text-center p-4">
                      <div className="w-12 h-12 bg-primary/10 rounded-full flex items-center justify-center mx-auto mb-3">
                        <item.icon className="w-6 h-6 text-primary" />
                      </div>
                      <p className="font-medium text-[#384451]">{item.title}</p>
                      <p className="text-xs text-slate-500">{item.desc}</p>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Properties Tab */}
        {activeTab === "properties" && (
          <div className="space-y-6">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-[#384451]">Mis Propiedades</h2>
              <Button onClick={() => setShowNewIntent(true)} className="bg-primary text-[#384451]">
                <Plus className="w-4 h-4 mr-2" /> Nueva Propiedad
              </Button>
            </div>

            {saleIntents.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Building className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <h3 className="text-lg font-medium text-[#384451] mb-2">No tienes propiedades registradas</h3>
                  <p className="text-slate-500 mb-4">Comienza registrando tu primera propiedad para vender</p>
                  <Button onClick={() => setShowNewIntent(true)} className="bg-primary text-[#384451]">
                    <Plus className="w-4 h-4 mr-2" /> Registrar Propiedad
                  </Button>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-4">
                {saleIntents.map(intent => (
                  <Card 
                    key={intent.id} 
                    className="overflow-hidden cursor-pointer hover:shadow-lg transition-shadow"
                    onClick={() => viewPropertyDetail({...intent, property: intent.property_info})}
                    data-testid={`property-intent-${intent.id}`}
                  >
                    <CardContent className="p-0">
                      <div className="flex">
                        {/* Property Image */}
                        <div className="w-40 h-40 bg-slate-200 flex items-center justify-center">
                          {intent.property_info?.images?.[0] ? (
                            <img src={intent.property_info.images[0]} alt="" className="w-full h-full object-cover" />
                          ) : (
                            (() => {
                              const Icon = PROPERTY_TYPE_ICONS[intent.property_type] || Home;
                              return <Icon className="w-12 h-12 text-slate-400" />;
                            })()
                          )}
                        </div>

                        {/* Property Details */}
                        <div className="flex-1 p-4">
                          <div className="flex items-start justify-between">
                            <div>
                              <h3 className="font-semibold text-lg text-[#384451] capitalize">
                                {intent.property_info?.title || `${intent.property_type} en ${intent.municipality}`}
                              </h3>
                              <p className="text-sm text-slate-500 flex items-center gap-1 mt-1">
                                <MapPin className="w-4 h-4" /> {intent.municipality}
                              </p>
                              {/* Property Features */}
                              {intent.property_info?.features && (
                                <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                                  {intent.property_info.features.bedrooms && (
                                    <span className="flex items-center gap-1"><Bed className="w-3 h-3" /> {intent.property_info.features.bedrooms}</span>
                                  )}
                                  {intent.property_info.features.bathrooms && (
                                    <span className="flex items-center gap-1"><Bath className="w-3 h-3" /> {intent.property_info.features.bathrooms}</span>
                                  )}
                                  {intent.property_info.features.area && (
                                    <span className="flex items-center gap-1"><Square className="w-3 h-3" /> {intent.property_info.features.area}m²</span>
                                  )}
                                </div>
                              )}
                            </div>
                            <StatusBadge status={intent.status} />
                          </div>

                          <Separator className="my-3" />

                          <div className="grid grid-cols-3 gap-4 text-sm">
                            <div>
                              <p className="text-slate-500">Agente</p>
                              <p className="font-medium">{intent.agent_name || "Por asignar"}</p>
                              {intent.agency_name && (
                                <p className="text-xs text-slate-400">{intent.agency_name}</p>
                              )}
                            </div>
                            <div>
                              <p className="text-slate-500">Comisión</p>
                              <p className="font-medium">{intent.commission_percentage}%</p>
                              <p className="text-xs text-slate-400">
                                {intent.commission_agreed ? "Acordada" : "Pendiente"}
                              </p>
                            </div>
                            {intent.estimated_price && (
                              <div>
                                <p className="text-slate-500">Precio Estimado</p>
                                <p className="font-medium">${intent.estimated_price.toLocaleString()}</p>
                              </div>
                            )}
                          </div>
                        </div>

                        {/* Actions */}
                        <div className="p-4 bg-slate-50 flex flex-col justify-center gap-2" onClick={(e) => e.stopPropagation()}>
                          {intent.status === "pending" && (
                            <Button 
                              size="sm" 
                              onClick={() => { setSelectedIntent(intent); setShowCommission(true); }}
                              className="bg-primary text-[#384451]"
                            >
                              <Handshake className="w-4 h-4 mr-1" /> Proponer Comisión
                            </Button>
                          )}
                          {intent.status === "agent_assigned" && !intent.commission_agreed && (
                            <Button 
                              size="sm" 
                              onClick={() => { setSelectedIntent(intent); setShowCommission(true); }}
                              className="bg-primary text-[#384451]"
                            >
                              <Handshake className="w-4 h-4 mr-1" /> Ajustar Comisión
                            </Button>
                          )}
                          {(intent.status === "property_listed" || intent.status === "matched") && (
                            <Button 
                              size="sm" 
                              variant="outline"
                              onClick={() => fetchPropertyStats(intent.id)}
                            >
                              <Eye className="w-4 h-4 mr-1" /> Ver Estadísticas
                            </Button>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}

            {/* Property Stats Dialog */}
            {propertyStats && (
              <Dialog open={!!propertyStats} onOpenChange={() => setPropertyStats(null)}>
                <DialogContent className="max-w-2xl">
                  <PropertyStatsCard stats={propertyStats} />
                </DialogContent>
              </Dialog>
            )}
          </div>
        )}

        {/* Valuation MVIP Tab */}
        {activeTab === "valuation" && (
          <ValuationModule />
        )}

        {/* Terreno Tab */}
        {activeTab === "terreno" && (
          <TerrenoModule />
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div className="space-y-6">
            <h2 className="text-2xl font-bold text-[#384451]">Notificaciones</h2>

            {notifications.length === 0 ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <Bell className="w-16 h-16 mx-auto text-slate-300 mb-4" />
                  <p className="text-slate-500">No tienes notificaciones</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-3">
                {notifications.map(notification => (
                  <Card 
                    key={notification.id} 
                    className={`cursor-pointer transition-all ${!notification.read ? "border-l-4 border-l-primary" : ""}`}
                    onClick={() => markNotificationRead(notification.id)}
                  >
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                          notification.type === "commission_accepted" ? "bg-green-100" :
                          notification.type === "property_listed" ? "bg-primary/10" :
                          notification.type === "representation_rejected" ? "bg-red-100" :
                          "bg-blue-100"
                        }`}>
                          {notification.type === "commission_accepted" ? <CheckCircle className="w-5 h-5 text-green-600" /> :
                           notification.type === "property_listed" ? <Building className="w-5 h-5 text-primary" /> :
                           notification.type === "representation_rejected" ? <X className="w-5 h-5 text-red-600" /> :
                           <Bell className="w-5 h-5 text-blue-600" />}
                        </div>
                        <div className="flex-1">
                          <p className="font-medium text-[#384451]">{notification.title}</p>
                          <p className="text-sm text-slate-500">{notification.message}</p>
                          <p className="text-xs text-slate-400 mt-2">
                            {new Date(notification.created_at).toLocaleDateString('es-MX', {
                              day: 'numeric',
                              month: 'short',
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </p>
                        </div>
                        {!notification.read && (
                          <div className="w-2 h-2 bg-primary rounded-full" />
                        )}
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}
      </main>

      {/* Commission Dialog */}
      <CommissionDialog 
        intent={showCommission ? selectedIntent : null}
        onClose={() => { setShowCommission(false); setSelectedIntent(null); }}
        onSubmit={handleProposeCommission}
      />

      {/* Property Detail Modal */}
      <PropertyDetailModal
        open={showPropertyDetailModal}
        onOpenChange={setShowPropertyDetailModal}
        property={selectedPropertyDetail?.property}
        statistics={selectedPropertyDetail?.statistics}
        canEdit={true}
        canDelete={true}
        onDelete={async (id) => {
          try {
            await axios.delete(`${API}/properties/${id}`);
            toast.success("Propiedad eliminada");
            fetchProperties();
            setShowPropertyDetailModal(false);
          } catch (e) {
            toast.error(e.response?.data?.detail || "Error al eliminar");
          }
        }}
        onUpdate={() => { fetchProperties(); }}
      />
    </div>
  );
};

export default OwnerDashboard;
